import React, { useState, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Alert,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Skeleton,
  TablePagination,
} from '@mui/material';
import {
  Assessment as ReportsIcon,
  TrendingUp as TrendingUpIcon,
  Inventory as InventoryIcon,
  MonetizationOn as SalesIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  FilterList as FilterIcon,
  Refresh as RefreshIcon,
  AccountBalance as CashDrawerIcon,
  SwapHoriz as TransferIcon,
  Payment as PayrollIcon,
  RequestQuote as AdvancesIcon,
  TrendingUp as CommissionsIcon,
  History as AuditIcon,
  Analytics as AnalyticsIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { formatCurrency } from '../utils/currency';
import { 
  useGetSalesReportQuery,
  useGetReportsSalesSummaryQuery,
  useGetInventoryReportQuery,
  useGetTopProductsQuery 
} from '../services/reports.service';
import { useGetLocationsQuery } from '../services/locations.service';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const ReportsPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [dateRange, setDateRange] = useState({
    from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    to: new Date(new Date().getTime() + 24 * 60 * 60 * 1000), // Tomorrow to ensure today's sales are included
  });
  const [selectedLocation, setSelectedLocation] = useState('all');
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  
  // Pagination states
  const [salesPage, setSalesPage] = useState(1);
  const [salesLimit, setSalesLimit] = useState(25);
  const [inventoryPage, setInventoryPage] = useState(1);
  const [inventoryLimit, setInventoryLimit] = useState(50);

  // Date range calculations
  const dateFilters = useMemo(() => {
    let from = dateRange.from;
    let to = dateRange.to;
    
    // Adjust dates based on selected period
    if (selectedPeriod === 'today') {
      from = new Date();
      to = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    } else if (selectedPeriod === 'week') {
      from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      to = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    } else if (selectedPeriod === 'month') {
      from = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
      to = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    } else if (selectedPeriod === 'quarter') {
      const currentMonth = new Date().getMonth();
      const quarterStart = Math.floor(currentMonth / 3) * 3;
      from = new Date(new Date().getFullYear(), quarterStart, 1);
      to = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    } else if (selectedPeriod === 'year') {
      from = new Date(new Date().getFullYear(), 0, 1);
      to = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
    }
    
    return {
      startDate: from.toISOString().split('T')[0],
      endDate: to.toISOString().split('T')[0],
      locationId: selectedLocation !== 'all' ? selectedLocation : undefined,
    };
  }, [dateRange, selectedLocation, selectedPeriod]);

  // API calls
  const { data: locationsResponse, isLoading: locationsLoading } = useGetLocationsQuery({ active: true });
  const { data: salesReport, isLoading: salesLoading, error: salesError, refetch: refetchSalesReport } = useGetSalesReportQuery({
    ...dateFilters,
    page: salesPage,
    limit: salesLimit,
  });
  const { data: salesSummary, isLoading: summaryLoading, refetch: refetchSalesSummary } = useGetReportsSalesSummaryQuery(dateFilters);
  const { data: inventoryReport, isLoading: inventoryLoading, refetch: refetchInventoryReport } = useGetInventoryReportQuery({
    locationId: selectedLocation !== 'all' ? selectedLocation : undefined,
    page: inventoryPage,
    limit: inventoryLimit,
  });
  const { data: topProducts, isLoading: topProductsLoading, refetch: refetchTopProducts } = useGetTopProductsQuery({
    ...dateFilters,
    limit: 20, // Show more top products
  });

  const locations = locationsResponse?.data || [];

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const exportReport = (format: 'pdf' | 'excel') => {
    const currentTabData = getCurrentTabName();
    console.log(`Exportando reporte ${currentTabData} en formato ${format}...`);
    // TODO: Implement actual export functionality
    alert(`📊 Reporte "${currentTabData}" exportado en formato ${format.toUpperCase()}`);
  };

  const printReport = () => {
    console.log('Imprimiendo reporte...');
    window.print();
  };

  const getCurrentTabName = () => {
    const tabNames = [
      'Resumen Ejecutivo',
      'Ventas Detalladas', 
      'Análisis de Inventario',
      'Rendimiento y KPIs',
      'Análisis Financiero',
      'Reportes Operacionales'
    ];
    return tabNames[currentTab] || 'Reporte';
  };

  const handlePeriodChange = (period: string) => {
    setSelectedPeriod(period);
    // Update date range based on period
    let from = new Date();
    let to = new Date();
    
    switch (period) {
      case 'today':
        from = new Date();
        to = new Date(new Date().getTime() + 24 * 60 * 60 * 1000); // Tomorrow to include all today's sales
        break;
      case 'week':
        from = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        to = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
        break;
      case 'month':
        from = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        to = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
        break;
      case 'quarter':
        const currentMonth = new Date().getMonth();
        const quarterStart = Math.floor(currentMonth / 3) * 3;
        from = new Date(new Date().getFullYear(), quarterStart, 1);
        to = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
        break;
      case 'year':
        from = new Date(new Date().getFullYear(), 0, 1);
        to = new Date(new Date().getTime() + 24 * 60 * 60 * 1000);
        break;
      default:
        return;
    }
    
    setDateRange({ from, to });
  };

  const refreshAllReports = async () => {
    await Promise.all([
      refetchSalesReport(),
      refetchSalesSummary(),
      refetchInventoryReport(),
      refetchTopProducts(),
    ]);
  };

  const getPaymentMethodIcon = (method: string) => {
    switch (method.toUpperCase()) {
      case 'CASH':
        return '💵';
      case 'CARD':
        return '💳';
      case 'TRANSFER':
        return '🏦';
      default:
        return '💰';
    }
  };

  const getPaymentMethodLabel = (method: string) => {
    switch (method.toUpperCase()) {
      case 'CASH':
        return 'Efectivo';
      case 'CARD':
        return 'Tarjeta';
      case 'TRANSFER':
        return 'Transferencia';
      default:
        return method;
    }
  };

  const getInventoryStatusColor = (status: string) => {
    switch (status) {
      case 'NEGATIVE':
        return 'error';
      case 'OUT_OF_STOCK':
        return 'error';
      case 'LOW_STOCK':
        return 'warning';
      default:
        return 'success';
    }
  };

  const getInventoryStatusLabel = (status: string) => {
    switch (status) {
      case 'NEGATIVE':
        return 'Negativo';
      case 'OUT_OF_STOCK':
        return 'Sin Stock';
      case 'LOW_STOCK':
        return 'Stock Bajo';
      default:
        return 'Normal';
    }
  };

  return (
    <Box sx={{ height: 'calc(100vh - 64px)', overflow: 'auto', pb: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            📊 Centro de Reportes
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Análisis integral del negocio • Ventas • Inventario • Finanzas • Operaciones
          </Typography>
        </Box>
        
        <Stack direction="row" spacing={1}>
          <Tooltip title="Actualizar todos los datos">
            <IconButton
              onClick={refreshAllReports}
              disabled={salesLoading || summaryLoading || inventoryLoading || topProductsLoading}
              color="primary"
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={<PrintIcon />}
            onClick={printReport}
          >
            Imprimir
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => exportReport('excel')}
          >
            Excel
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => exportReport('pdf')}
          >
            PDF
          </Button>
        </Stack>
      </Box>

      {/* Filtros Mejorados */}
      <Paper sx={{ p: 2.5, mb: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FilterIcon color="primary" />
          <Typography variant="h6" fontWeight="bold" color="primary">
            Filtros y Período de Análisis
          </Typography>
        </Box>

        <Grid container spacing={2} alignItems="center">
          {/* Períodos predefinidos */}
          <Grid size={{ xs: 12, md: 8 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="body2" sx={{ alignSelf: 'center', mr: 1, fontWeight: 'medium' }}>Período:</Typography>
              {[
                { label: 'Hoy', value: 'today' },
                { label: 'Última semana', value: 'week' },
                { label: 'Este mes', value: 'month' },
                { label: 'Trimestre', value: 'quarter' },
                { label: 'Año', value: 'year' },
                { label: 'Personalizado', value: 'custom' }
              ].map((period) => (
                <Button
                  key={period.value}
                  variant={selectedPeriod === period.value ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => handlePeriodChange(period.value)}
                  sx={{ minWidth: 'auto' }}
                >
                  {period.label}
                </Button>
              ))}
            </Box>
          </Grid>
          
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel>🏢 Ubicación</InputLabel>
              <Select
                value={selectedLocation}
                onChange={(e) => setSelectedLocation(e.target.value)}
                label="🏢 Ubicación"
                disabled={locationsLoading}
              >
                <MenuItem value="all">📊 Todas las ubicaciones</MenuItem>
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    🏪 {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Fechas personalizadas (solo si está seleccionado) */}
          {selectedPeriod === 'custom' && (
            <>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="📅 Fecha Desde"
                  type="date"
                  value={dateRange.from.toISOString().split('T')[0]}
                  onChange={(e) => setDateRange({
                    ...dateRange,
                    from: new Date(e.target.value),
                  })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="📅 Fecha Hasta"
                  type="date"
                  value={dateRange.to.toISOString().split('T')[0]}
                  onChange={(e) => setDateRange({
                    ...dateRange,
                    to: new Date(e.target.value),
                  })}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
            </>
          )}
        </Grid>
      </Paper>

      {/* Resumen ejecutivo */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SalesIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Ventas Totales
                </Typography>
              </Box>
              {summaryLoading ? (
                <Skeleton height={40} />
              ) : (
                <>
                  <Typography variant="h4" color="primary" fontWeight="bold">
                    {formatCurrency(salesSummary?.data?.totalSales || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {salesSummary?.data?.transactionCount || 0} transacciones
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TrendingUpIcon color="secondary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Ticket Promedio
                </Typography>
              </Box>
              {summaryLoading ? (
                <Skeleton height={40} />
              ) : (
                <>
                  <Typography variant="h4" color="secondary" fontWeight="bold">
                    {formatCurrency(salesSummary?.data?.averageTicket || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Por transacción
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <InventoryIcon color="warning" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Valor Inventario
                </Typography>
              </Box>
              {inventoryLoading ? (
                <Skeleton height={40} />
              ) : (
                <>
                  <Typography variant="h4" color="warning.main" fontWeight="bold">
                    {formatCurrency(inventoryReport?.data?.summary?.totalValue || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {inventoryReport?.pagination?.total || inventoryReport?.data?.summary?.totalItems || 0} productos
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <ReportsIcon color="success" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Productos Críticos
                </Typography>
              </Box>
              {inventoryLoading ? (
                <Skeleton height={40} />
              ) : (
                <>
                  <Typography variant="h4" color="error.main" fontWeight="bold">
                    {(inventoryReport?.data?.summary?.lowStockCount || 0) + 
                     (inventoryReport?.data?.summary?.outOfStockCount || 0) +
                     (inventoryReport?.data?.summary?.negativeStockCount || 0)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Requieren atención
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Pestañas de reportes expandidas */}
      <Paper sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange} 
            aria-label="report tabs"
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                minHeight: 64,
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 'medium'
              }
            }}
          >
            <Tab 
              label="📊 Resumen Ejecutivo" 
              icon={<AnalyticsIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="💰 Ventas Detalladas" 
              icon={<SalesIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="📦 Análisis de Inventario" 
              icon={<InventoryIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="📈 Rendimiento y KPIs" 
              icon={<TrendingUpIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="💼 Análisis Financiero" 
              icon={<BusinessIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="⚙️ Reportes Operacionales" 
              icon={<ReportsIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Panel de Resumen Ejecutivo */}
        <TabPanel value={currentTab} index={0}>
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AnalyticsIcon color="primary" />
            Resumen Ejecutivo del Negocio
          </Typography>
          
          {/* Métricas principales en cards más atractivas */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>💰 Ventas Totales</Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {formatCurrency(salesSummary?.data?.totalSales || 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {salesSummary?.data?.transactionCount || 0} transacciones
                      </Typography>
                    </Box>
                    <SalesIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>📊 Ticket Promedio</Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {formatCurrency(salesSummary?.data?.averageTicket || 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Por transacción</Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>📦 Valor Inventario</Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {formatCurrency(inventoryReport?.data?.summary?.totalValue || 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        {inventoryReport?.pagination?.total || 0} productos
                      </Typography>
                    </Box>
                    <InventoryIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
              <Card sx={{ background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', color: 'white' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography variant="h6" sx={{ opacity: 0.9, mb: 1 }}>⚠️ Productos Críticos</Typography>
                      <Typography variant="h4" fontWeight="bold">
                        {(inventoryReport?.data?.summary?.lowStockCount || 0) + 
                         (inventoryReport?.data?.summary?.outOfStockCount || 0) +
                         (inventoryReport?.data?.summary?.negativeStockCount || 0)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>Requieren atención</Typography>
                    </Box>
                    <ReportsIcon sx={{ fontSize: 48, opacity: 0.3 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Gráficos y análisis rápido */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    🏪 Rendimiento por Ubicación
                  </Typography>
                  {summaryLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Box>
                      {salesSummary?.data?.salesByLocation?.map((locationSale, index) => (
                        <Box key={locationSale.locationId} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {locationSale.location?.name || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="primary" fontWeight="bold">
                              {formatCurrency(locationSale._sum.total || 0)}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {locationSale._count} transacciones
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    💳 Métodos de Pago Populares
                  </Typography>
                  {summaryLoading ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Box>
                      {salesSummary?.data?.salesByPaymentMethod?.map((paymentMethod) => (
                        <Box key={paymentMethod.paymentMethod} sx={{ mb: 2 }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                            <Typography variant="body2" fontWeight="medium">
                              {getPaymentMethodIcon(paymentMethod.paymentMethod)} {getPaymentMethodLabel(paymentMethod.paymentMethod)}
                            </Typography>
                            <Typography variant="body2" color="primary" fontWeight="bold">
                              {formatCurrency(paymentMethod._sum.total || 0)}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {paymentMethod._count} transacciones
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Panel de Ventas Detalladas */}
        <TabPanel value={currentTab} index={1}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Reporte de Ventas Detalladas
          </Typography>
          
          {salesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : salesError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              Error al cargar el reporte de ventas
            </Alert>
          ) : (
            <>
              <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto', border: 1, borderColor: 'divider' }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Fecha/Hora</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Usuario</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Ubicación</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Pago</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Items</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Subtotal</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Descuento</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {salesReport?.data?.map((sale) => (
                      <TableRow key={sale.id} hover>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(sale.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(sale.createdAt).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{sale.user?.name || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{sale.location?.name || 'N/A'}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={`${getPaymentMethodIcon(sale.paymentMethod)} ${getPaymentMethodLabel(sale.paymentMethod)}`}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{sale.saleItems?.length || 0}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{formatCurrency(sale.subtotal)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{formatCurrency(sale.discount)}</Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="primary.main">
                            {formatCurrency(sale.total)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              {salesReport?.pagination && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{salesReport.pagination.total}</strong> transacciones en total
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      Total mostrado: <strong>{formatCurrency(salesReport.data?.reduce((sum, s) => sum + s.total, 0) || 0)}</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TablePagination
                      component="div"
                      count={salesReport.pagination.total}
                      page={salesPage - 1}
                      onPageChange={(e, newPage) => setSalesPage(newPage + 1)}
                      rowsPerPage={salesLimit}
                      onRowsPerPageChange={(e) => {
                        setSalesLimit(parseInt(e.target.value));
                        setSalesPage(1);
                      }}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      labelRowsPerPage="Filas por página:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </TabPanel>

        {/* Panel de Inventario */}
        <TabPanel value={currentTab} index={2}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Reporte de Inventario
          </Typography>
          
          {inventoryLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : inventoryReport?.data?.summary && (
            <>
              <Alert 
                severity={
                  (inventoryReport.data.summary.lowStockCount + 
                   inventoryReport.data.summary.outOfStockCount + 
                   inventoryReport.data.summary.negativeStockCount) > 0 ? 'warning' : 'info'
                } 
                sx={{ mb: 2 }}
              >
                <strong>{inventoryReport.data.summary.totalItems} productos</strong> en inventario.
                {inventoryReport.data.summary.lowStockCount > 0 && ` ${inventoryReport.data.summary.lowStockCount} con stock bajo.`}
                {inventoryReport.data.summary.outOfStockCount > 0 && ` ${inventoryReport.data.summary.outOfStockCount} sin stock.`}
                {inventoryReport.data.summary.negativeStockCount > 0 && ` ${inventoryReport.data.summary.negativeStockCount} con inventario negativo.`}
              </Alert>
              
              <TableContainer sx={{ maxHeight: '70vh', overflow: 'auto', border: 1, borderColor: 'divider' }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Producto</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>SKU</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Categoría</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Ubicación</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Stock</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Stock Mín</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Costo</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Precio</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Valor Total</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', bgcolor: 'background.paper' }}>Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {inventoryReport.data.items?.map((item) => (
                      <TableRow key={item.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {item.product.name}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption" color="text.secondary">
                            {item.product.sku}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.product.category}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {item.location.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            color={
                              item.quantity < 0 ? 'error' : 
                              item.quantity <= item.product.minStock ? 'warning.main' : 'inherit'
                            }
                            fontWeight={item.quantity <= item.product.minStock ? 'bold' : 'normal'}
                          >
                            {item.quantity} {item.product.unit}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="caption" color="text.secondary">
                            {item.product.minStock}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(item.product.costPrice)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {formatCurrency(item.product.salePrice)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            color={item.value < 0 ? 'error' : 'primary.main'}
                            fontWeight="medium"
                          >
                            {formatCurrency(item.value)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={getInventoryStatusLabel(item.status)}
                            size="small"
                            color={getInventoryStatusColor(item.status) as any}
                            variant="outlined"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Inventory Pagination */}
              {inventoryReport?.pagination && (
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      <strong>{inventoryReport.pagination.total}</strong> productos en inventario
                    </Typography>
                    <Typography variant="body2" color="primary" fontWeight="bold">
                      Valor total: <strong>{formatCurrency(inventoryReport.data?.summary?.totalValue || 0)}</strong>
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TablePagination
                      component="div"
                      count={inventoryReport.pagination.total}
                      page={inventoryPage - 1}
                      onPageChange={(e, newPage) => setInventoryPage(newPage + 1)}
                      rowsPerPage={inventoryLimit}
                      onRowsPerPageChange={(e) => {
                        setInventoryLimit(parseInt(e.target.value));
                        setInventoryPage(1);
                      }}
                      rowsPerPageOptions={[25, 50, 100, 200]}
                      labelRowsPerPage="Filas por página:"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                    />
                  </Box>
                </Box>
              )}
            </>
          )}
        </TabPanel>

        {/* Panel de Rendimiento y KPIs */}
        <TabPanel value={currentTab} index={3}>
          <Typography variant="h6" gutterBottom fontWeight="bold">
            Análisis de Rendimiento
          </Typography>
          
          <Grid container spacing={3}>
            {/* Métricas por ubicación */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Ventas por Ubicación
                  </Typography>
                  {summaryLoading ? (
                    <Skeleton height={80} />
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      {salesSummary?.data?.salesByLocation?.map((locationSale) => (
                        <Box key={locationSale.locationId} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">{locationSale.location?.name || 'N/A'}</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(locationSale._sum.total || 0)} ({locationSale._count} ventas)
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Métricas por método de pago */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Métodos de Pago
                  </Typography>
                  {summaryLoading ? (
                    <Skeleton height={80} />
                  ) : (
                    <Box sx={{ mb: 2 }}>
                      {salesSummary?.data?.salesByPaymentMethod?.map((paymentMethod) => (
                        <Box key={paymentMethod.paymentMethod} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Typography variant="body2">
                            {getPaymentMethodIcon(paymentMethod.paymentMethod)} {getPaymentMethodLabel(paymentMethod.paymentMethod)}
                          </Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(paymentMethod._sum.total || 0)} ({paymentMethod._count} ventas)
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Top productos */}
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Productos Más Vendidos
                  </Typography>
                  {topProductsLoading ? (
                    <Skeleton height={200} />
                  ) : (
                    <TableContainer sx={{ maxHeight: 400, overflow: 'auto' }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Posición</TableCell>
                            <TableCell>Producto</TableCell>
                            <TableCell>SKU</TableCell>
                            <TableCell>Categoría</TableCell>
                            <TableCell align="right">Cantidad Vendida</TableCell>
                            <TableCell align="right">Ingresos</TableCell>
                            <TableCell align="right">% del Total</TableCell>
                            <TableCell align="right">Ventas</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {topProducts?.data?.map((product) => (
                            <TableRow key={product.product?.id}>
                              <TableCell>{product.rank}</TableCell>
                              <TableCell>{product.product?.name || 'N/A'}</TableCell>
                              <TableCell>{product.product?.sku || 'N/A'}</TableCell>
                              <TableCell>{product.product?.category || 'N/A'}</TableCell>
                              <TableCell align="right">{product.quantitySold}</TableCell>
                              <TableCell align="right">{formatCurrency(product.revenue)}</TableCell>
                              <TableCell align="right">{product.percentage.toFixed(1)}%</TableCell>
                              <TableCell align="right">{product.salesCount}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Panel de Análisis Financiero */}
        <TabPanel value={currentTab} index={4}>
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BusinessIcon color="primary" />
            Análisis Financiero Integral
          </Typography>
          
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              💡 <strong>Próximamente:</strong> Reportes de comisiones, planilla, adelantos y análisis de rentabilidad.
              Estas funcionalidades están siendo desarrolladas para proporcionar un análisis financiero completo.
            </Typography>
          </Alert>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 4 }}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <CommissionsIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    📊 Comisiones
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Análisis detallado de comisiones por vendedor y período
                  </Typography>
                  <Button variant="outlined" size="small" disabled>
                    Próximamente
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <PayrollIcon sx={{ fontSize: 48, color: 'secondary.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    💼 Planilla
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Reportes de nómina y costos laborales
                  </Typography>
                  <Button variant="outlined" size="small" disabled>
                    Próximamente
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 4 }}>
              <Card variant="outlined">
                <CardContent sx={{ textAlign: 'center' }}>
                  <AdvancesIcon sx={{ fontSize: 48, color: 'warning.main', mb: 2 }} />
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    💰 Adelantos
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Control y seguimiento de adelantos de sueldo
                  </Typography>
                  <Button variant="outlined" size="small" disabled>
                    Próximamente
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Panel de Reportes Operacionales */}
        <TabPanel value={currentTab} index={5}>
          <Typography variant="h5" gutterBottom fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ReportsIcon color="primary" />
            Reportes Operacionales del Sistema
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph>
            Monitoreo y análisis de todas las operaciones del sistema para optimizar el rendimiento del negocio.
          </Typography>
          
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CashDrawerIcon sx={{ mr: 1, color: 'success.main' }} />
                    <Typography variant="h6" fontWeight="bold">
                      💰 Caja Registradora
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Movimientos de caja, arqueos y control de efectivo por turno y ubicación.
                  </Typography>
                  <Button variant="outlined" size="small" disabled>
                    En desarrollo
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <TransferIcon sx={{ mr: 1, color: 'info.main' }} />
                    <Typography variant="h6" fontWeight="bold">
                      🔄 Transferencias
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Movimientos de inventario entre ubicaciones y análisis de logística.
                  </Typography>
                  <Button variant="outlined" size="small" disabled>
                    En desarrollo
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12, md: 6, lg: 4 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <AuditIcon sx={{ mr: 1, color: 'warning.main' }} />
                    <Typography variant="h6" fontWeight="bold">
                      📋 Auditoría
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Logs de actividad, cambios en el sistema y trazabilidad de operaciones.
                  </Typography>
                  <Button variant="outlined" size="small" disabled>
                    En desarrollo
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    🔧 Estado Actual del Sistema de Reportes
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Chip 
                        label="✅ Reportes de Ventas" 
                        color="success" 
                        variant="outlined" 
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Chip 
                        label="✅ Análisis de Inventario" 
                        color="success" 
                        variant="outlined" 
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Chip 
                        label="🔄 Reportes Financieros" 
                        color="warning" 
                        variant="outlined" 
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                      <Chip 
                        label="🔄 Reportes Operacionales" 
                        color="warning" 
                        variant="outlined" 
                        sx={{ width: '100%' }}
                      />
                    </Grid>
                  </Grid>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    ℹ️ El sistema se encuentra en constante desarrollo. Los reportes marcados con 🔄 están siendo implementados 
                    y estarán disponibles en próximas actualizaciones.
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default ReportsPage;